import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import type { SmtpAccount } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, TextInput, TextArea } from '@/components/ui/Field';
import { replySubjectLine } from '@marketing-hub/shared';
import leadStyles from '@/features/leads/Leads.module.css';

type Props = {
  messageId: string;
  replyToLabel: string;
  originalSubject: string | null;
  leadId: string | null;
  defaultMailboxId: string | null;
  onCancel: () => void;
  onSent?: () => void;
};

export function InboxReplyComposer({
  messageId,
  replyToLabel,
  originalSubject,
  leadId,
  defaultMailboxId,
  onCancel,
  onSent,
}: Props) {
  const qc = useQueryClient();
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const [subject, setSubject] = useState(() => replySubjectLine(originalSubject));
  const [body, setBody] = useState('');
  const [mailboxId, setMailboxId] = useState(defaultMailboxId ?? '');

  useEffect(() => {
    setSubject(replySubjectLine(originalSubject));
    setMailboxId(defaultMailboxId ?? '');
    setBody('');
  }, [messageId, originalSubject, defaultMailboxId]);

  const accountsQ = useQuery({
    queryKey: ['smtp-accounts', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ accounts: SmtpAccount[] }>(`/api/smtp-accounts?project_id=${projectId}`),
  });

  const precheckQ = useQuery({
    queryKey: ['mail-precheck', leadId, mailboxId],
    enabled: Boolean(leadId),
    queryFn: () => {
      const p = new URLSearchParams({ lead_id: leadId! });
      if (mailboxId) p.set('smtp_account_id', mailboxId);
      return apiFetch<{ canSend: boolean; warnings: string[]; blockReason?: string }>(
        `/api/mail/send-precheck?${p}`,
      );
    },
  });

  const send = useMutation({
    mutationFn: () =>
      apiFetch<{ messageId: string; status: string; warnings?: string[] }>(
        `/api/mail/messages/${messageId}/reply`,
        {
          method: 'POST',
          body: JSON.stringify({
            subject,
            body_text: body,
            smtp_account_id: mailboxId || undefined,
            lead_id: leadId ?? undefined,
          }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
      void qc.invalidateQueries({ queryKey: ['mail-message', messageId] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      onSent?.();
      onCancel();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    send.mutate();
  }

  const accounts = accountsQ.data?.accounts ?? [];
  const precheckBlocked =
    leadId &&
    precheckQ.data?.canSend === false &&
    precheckQ.data.blockReason !== 'Rechtsgrond verplicht voor mail';

  return (
    <form className={leadStyles.composeMail} onSubmit={(e) => void submit(e)}>
      <h3>Beantwoorden</h3>
      <p className={leadStyles.muted}>Naar: {replyToLabel}</p>
      {precheckQ.data?.warnings?.length ? (
        <ul className={leadStyles.muted}>
          {precheckQ.data.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      {send.data?.warnings?.length ? (
        <ul className={leadStyles.muted}>
          {send.data.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      {accounts.length > 0 ? (
        <Field label="Mailbox">
          <select
            className={leadStyles.filterInput}
            value={mailboxId}
            onChange={(e) => setMailboxId(e.target.value)}
          >
            <option value="">Standaard</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label} ({a.from_email})
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="Onderwerp">
        <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} required />
      </Field>
      <Field label="Bericht">
        <TextArea value={body} onChange={(e) => setBody(e.target.value)} required rows={8} />
      </Field>
      <p className={leadStyles.signaturePreview}>
        Handtekening en compliance worden door de worker toegevoegd. Thread-headers (In-Reply-To)
        worden gezet voor correcte e-mailthreads.
      </p>
      <div className={leadStyles.composeActions}>
        <Button type="submit" disabled={send.isPending || Boolean(precheckBlocked)}>
          {send.isPending ? 'Verzenden…' : 'Versturen'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuleren
        </Button>
      </div>
      {send.isError ? (
        <p role="alert">
          {send.error instanceof ApiError && send.error.code === 'MAILBOX_LIMIT'
            ? send.error.message
            : send.error instanceof ApiError
              ? send.error.message
              : 'Verzenden mislukt. Controleer mailbox en worker.'}
        </p>
      ) : null}
      {send.isSuccess ? (
        <p className={leadStyles.muted}>Antwoord staat in de wachtrij; verschijnt onder Verzonden.</p>
      ) : null}
    </form>
  );
}
